import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Zap } from "lucide-react";
import { api, fmt, trackClicSoumission } from "../lib/api";
import { lp } from "../lib/localize";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";
import { ShareButtons } from "../components/ShareButtons";
import { ELink } from "../components/Editable";

export default function BlogPost() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [products, setProducts] = useState([]);  // produits suggérés (maillage boutique)

  useEffect(() => {
    setPost(null);
    setRelated([]);
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);

  // Articles liés : mots-clés du titre en commun, sinon les plus récents
  useEffect(() => {
    if (!post) return;
    const STOP = new Set(["le", "la", "les", "de", "du", "des", "un", "une", "et", "en", "au", "aux", "pour", "sur", "avec", "the", "and", "for", "with", "your", "how", "what", "a", "an", "of", "to", "in", "on"]);
    const postTitle = (lang === "en" && post.title_en ? post.title_en : post.title) || "";
    const words = new Set(postTitle.toLowerCase().split(/[^\p{L}]+/u).filter((w) => w.length > 3 && !STOP.has(w)));
    api.get("/posts").then((r) => {
      const scored = r.data
        .filter((p) => p.slug !== slug)
        .map((p) => {
          const t2 = ((lang === "en" && p.title_en ? p.title_en : p.title) || "").toLowerCase();
          const score = t2.split(/[^\p{L}]+/u).filter((w) => words.has(w)).length;
          return { p, score };
        })
        .sort((a, b) => b.score - a.score || new Date(b.p.created_at || 0) - new Date(a.p.created_at || 0))
        .slice(0, 3);
      setRelated(scored.map((s) => s.p));
    }).catch(() => {});
  }, [post, slug, lang]);

  // Maillage boutique : produits vedettes suggérés sous chaque article (SEO interne + ventes)
  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 3 } })
      .then((r) => setProducts(r.data || []))
      .catch(() => {});
  }, []);

  if (post === false) return (
    <div data-testid="post-not-found" className="min-h-[60vh] flex flex-col items-center justify-center px-6 pt-24 pb-16 text-center">
      <h1 className="font-serif text-4xl mb-8">{t("blog.not_found")}</h1>
      <ELink editPath="link.blog_back" to="/blogue" className="inline-flex items-center gap-3 bg-gold text-ink font-semibold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300">
        {t("blog.back")}
      </ELink>
    </div>
  );
  if (!post) return null;

  const title = lang === "en" && post.title_en ? post.title_en : post.title;
  const excerpt = lang === "en" && post.excerpt_en ? post.excerpt_en : post.excerpt;
  const content = lang === "en" && post.content_en ? post.content_en : post.content;
  // Hygiène SEO (rapport Bing) sur le contenu IA injecté en HTML brut : jamais de
  // <h1> dans le corps (un seul h1 par page) et un alt sur chaque image
  const safeContent = (content || "")
    .replace(/<h1(\s[^>]*)?>/gi, "<h2$1>").replace(/<\/h1>/gi, "</h2>")
    .replace(/<img(?![^>]*\balt=)/gi, '<img alt="" ');

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: excerpt || title,
    image: post.image?.startsWith("http") ? post.image : `https://mrtrophee.ca${post.image}`,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Organization", name: "Mr Trophée", url: "https://mrtrophee.ca" },
    publisher: { "@type": "Organization", name: "Mr Trophée", url: "https://mrtrophee.ca" },
    inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
    mainEntityOfPage: `https://mrtrophee.ca/blogue/${post.slug}`,
  };

  return (
    <div data-testid="blog-post-page" className="pt-32 pb-28">
      <SEO
        title={`${title} | Mr Trophée`}
        description={(excerpt || title).slice(0, 155)}
        path={`/blogue/${post.slug}`}
        image={post.image || "/logo-full.png"}
        jsonLd={articleLd}
        keywords={(lang === "en" ? post.seo_keywords_en : post.seo_keywords) || post.seo_keywords}
      />
      <article className="max-w-3xl mx-auto px-6">
        <FadeUp>
          <ELink editPath="link.blog_back" to="/blogue" data-testid="post-back" className="inline-flex items-center gap-2 text-ivory/70 hover:text-gold text-sm mb-10 transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" /> {t("blog.back")}
          </ELink>
          <p className="text-xs text-gold tracking-[0.25em] uppercase mb-4">
            {post.created_at ? new Date(post.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { timeZone: "America/Montreal", year: "numeric", month: "long", day: "numeric" }) : ""}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-10">{title}</h1>
        </FadeUp>
        {post.image && (
          <FadeUp delay={0.1}>
            <img src={post.image} alt={title} className="w-full rounded-xl border border-ivory/10 mb-10 object-cover max-h-[420px]" />
          </FadeUp>
        )}
        {post.video && (
          <FadeUp delay={0.12}>
            <video data-testid="post-video" src={post.video} controls playsInline preload="metadata"
              className="w-full rounded-xl border border-gold/20 mb-10" />
          </FadeUp>
        )}
        <FadeUp delay={0.15}>
          {/<[a-z][\s\S]*>/i.test(content) ? (
            <div data-testid="post-content" className="post-html text-ivory/80 leading-relaxed text-lg"
              dangerouslySetInnerHTML={{ __html: safeContent }} />
          ) : (
            <div data-testid="post-content" className="text-ivory/80 leading-relaxed text-lg whitespace-pre-line">{content}</div>
          )}
        </FadeUp>
        {/* Gros bouton « Soumission en 30 secondes » en fin d'article (demande du
            propriétaire, 2026-10-17) — capter le lecteur au moment chaud */}
        <FadeUp delay={0.18}>
          <div className="mt-12 text-center" data-testid="post-quote-cta">
            <Link
              to="/soumission-rapide" data-testid="post-quick-quote"
              onClick={() => trackClicSoumission("blogue")}
              className="group inline-flex items-center gap-3 bg-gold text-ink font-bold text-lg px-10 md:px-12 py-5 rounded-full shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:bg-champagne hover:shadow-[0_0_55px_rgba(212,175,55,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
            >
              <Zap className="w-6 h-6 transition-transform duration-300 group-hover:-rotate-12" />
              {t("quote.fab_long")}
              <ArrowRight className="w-6 h-6 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
        <FadeUp delay={0.2}>
          <div className="mt-14 pt-8 border-t border-ivory/10" data-testid="post-share">
            <p className="text-sm text-ivory/70 mb-4">{t("blog.share")}</p>
            <ShareButtons title={title} />
          </div>
        </FadeUp>
        {related.length > 0 && (
          <FadeUp delay={0.25}>
            <div className="mt-14 pt-8 border-t border-ivory/10" data-testid="related-articles">
              <h2 className="font-serif text-2xl mb-6">{t("blog.related")}</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((rp, i) => {
                  const rTitle = lang === "en" && rp.title_en ? rp.title_en : rp.title;
                  const rExcerpt = lang === "en" && rp.excerpt_en ? rp.excerpt_en : rp.excerpt;
                  return (
                    <Link key={rp.slug} to={`/blogue/${rp.slug}`} data-testid={`related-${i}`}
                      className="group bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/40 transition-colors duration-500">
                      {rp.image ? (
                        <img src={rp.image} alt="" className="w-full h-28 object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-28 bg-gold/10 flex items-center justify-center">
                          <span className="font-serif text-3xl text-gold/40">M</span>
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-serif text-sm leading-snug mb-2 group-hover:text-gold transition-colors duration-300 line-clamp-2">{rTitle}</h3>
                        <p className="text-xs text-ivory/70 line-clamp-2">{rExcerpt}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </FadeUp>
        )}
        {/* Produits suggérés : maillage boutique (SEO interne + conversion) */}
        {products.length > 0 && (
          <FadeUp delay={0.3}>
            <div className="mt-14 pt-8 border-t border-ivory/10" data-testid="suggested-products">
              <h2 className="font-serif text-2xl mb-6">{t("blog.shop_title")}</h2>
              <div className="grid grid-cols-3 gap-4">
                {products.map((p, i) => (
                  <Link key={p.id} to={`/produit/${p.id}`} data-testid={`suggested-product-${i}`}
                    className="group bg-surface border border-ivory/10 rounded-xl overflow-hidden hover:border-gold/40 transition-colors duration-300">
                    <div className="bg-white aspect-square flex items-center justify-center p-3">
                      <img src={p.image} alt={lp(p, lang).name} loading="lazy"
                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-ivory line-clamp-2 min-h-[2.4em]">{lp(p, lang).name}</p>
                      <p className="text-gold font-serif mt-1">{t("catalogue.grid_from", { price: fmt(p.base_price) })}</p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-6">
                <ELink editPath="link.blog_shop" to="/boutique" data-testid="suggested-shop-link"
                  className="inline-flex items-center gap-2 text-sm text-gold hover:text-champagne transition-colors duration-300">
                  {t("blog.shop_cta")} →
                </ELink>
              </div>
            </div>
          </FadeUp>
        )}
      </article>
    </div>
  );
}
